import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  UiComponents,
  UnoStoreHooks,
  LockIcon,
  CheckSolidIcon,
  CancelSolidIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@simulanisindia/ui";
import { useModalContext } from "./context/ModalProvider";
import { OLD_PASSWORD, NEW_PASSWORD, CONFIRM_PASSWORD } from "../constant/constants";
import apiClient from "../apiClient";
import { FORGOT_PASSWORD, UPDATE_PASSWORD } from "../apiClient/apiEndpoints";
import BtnLoad from "../components/BtnLoad";
import { getPasswordValidationSchema, passwordRequirements } from "../utils/general";
import PasswordStrength from "../pages/Password/passwordStrength";
import { RESET_PASSWORD_MSG } from "../constant/message";
import { CircleLoading } from "../Icons";

const { SimInputBar } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

const fields = {
  currentPassword: "Current Password*",
  newPassword: "New Password*",
  confirmPassword: "Confirm New Password*",
};

const PasswordChangeModal = () => {
  const configData = useUnoSelector((state) => state.config);
  const userData = useUnoSelector((state) => state.user);
  const currentUserUUID = userData?.user?.uuid;
  let passwordPolicy = configData.config.passwordPolicy;
  const { modalState, closeModal } = useModalContext();
  const UUID = modalState.params.uuid;
  const { user } = modalState;
  const [showPassword, setShowPassword] = useState({
    [OLD_PASSWORD]: false,
    [NEW_PASSWORD]: false,
    [CONFIRM_PASSWORD]: false,
  });

  const [error, setError] = useState(null);
  const [resetLinkMsg, setResetLinkMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);

  const togglePasswordVisibility = (type) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [type]: !prevState[type],
    }));
  };

  const handleCancelModal = async () => {
    setShowPassword({
      [OLD_PASSWORD]: false,
      [NEW_PASSWORD]: false,
      [CONFIRM_PASSWORD]: false,
    });
    closeModal();
  };

  const validationSchema = Yup.object({
    currentPassword: currentUserUUID != UUID ? Yup.string() : Yup.string().required("Current password is required"),
    newPassword: getPasswordValidationSchema(passwordPolicy),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (loading) return;
        setError(null);
        setLoading(true);
        let bodyData = {
          oldPassword: values.currentPassword,
          password: values.newPassword,
          confirmPassword: values.confirmPassword,
        };
        await apiClient.post(`${configData.config.UNO_URL}${UPDATE_PASSWORD}/${UUID}`, bodyData);
      } catch (error) {
        console.log(error);
        setError(error.response.data.message || error.message);
      } finally {
        setLoading(false);
        handleCancelModal()
      }
    },
  });

  const handleForgotPasswordClick = async () => {
    if (sendEmailLoading) return;
    try {
      setSendEmailLoading(true);
      let bodyData = { email: user.email };
      await apiClient.post(`${configData.config.UNO_URL}${FORGOT_PASSWORD}`, bodyData);
      setResetLinkMsg(RESET_PASSWORD_MSG);
    } catch (error) {
      console.log(error);
    } finally {
      setSendEmailLoading(false);
    }
  };

  const requirements = passwordRequirements(passwordPolicy);

  return (
    <div className="changepassword p-4">
      <div className="changepasswordheader">
        <LockIcon aria-label="Lock Icon" />
        <div>
          <h5>Change Password</h5>
          <p>Update password for enhanced account security.</p>
        </div>
      </div>
      <form onSubmit={formik.handleSubmit} className="mt-3">
        {Object.keys(fields)
          .filter((e) => {
            if (currentUserUUID != UUID && e == "currentPassword") return false;
            return true;
          })
          .map((field, index) => (
            <div className="mt-3" key={index}>
              <label className="mb-1">
                {field === "currentPassword"
                  ? "Current Password*"
                  : field === "newPassword"
                  ? "New Password*"
                  : "Confirm New Password*"}
              </label>
              <div className="input-icon">
                <SimInputBar
                  type={showPassword[field] ? "text" : "password"}
                  placeholder="********"
                  {...formik.getFieldProps(field)}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility(field)}
                  className="password-toggle"
                  aria-label={showPassword[field] ? "Hide password" : "Show password"}
                >
                  {showPassword[field] ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>
              {formik.touched[field] && formik.errors[field] && (
                <p className="text-danger m-0">{formik.errors[field]}</p>
              )}
            </div>
          ))}
        <div className="line">
          <PasswordStrength requirements={requirements} password={formik.values.newPassword} />
        </div>

        <p className="my-3 pass-contain">Password must contain:</p>
        <ul className="check-btn">
          {passwordPolicy &&
            requirements.map((req, index) => {
              const isValid = req.regex.test(formik.values.newPassword);
              return (
                <li key={index} className={isValid ? "valid" : "invalid"}>
                  {isValid ? (
                    <CheckSolidIcon aria-label="Valid" className="check-icon" />
                  ) : (
                    <CancelSolidIcon aria-label="Invalid" className="cancel-icon" />
                  )}
                  {req.text}
                </li>
              );
            })}
        </ul>

        <div className="modal-footer p-0 justify-content-between">
          {user.email && (
            <p onClick={handleForgotPasswordClick} className="forgotBtn" style={{ cursor: "pointer" }}>
              {sendEmailLoading && <BtnLoad />}
              Forgot Password?
            </p>
          )}

          <div className="modal-footer-btns">
            <button onClick={handleCancelModal} type="button" className="cancel">
              Cancel
            </button>
            {loading ? (
              <button className="save">
                {" "}
                <BtnLoad />{" "}
              </button>
            ) : (
              <button type="submit" className="save">
                Apply Changes
              </button>
            )}
          </div>
          {resetLinkMsg && <p style={{ color: "red" }}> {resetLinkMsg} </p>}
          {error && (
            <p className="modal-error" style={{ color: "red" }}>
              {" "}
              {error}{" "}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default PasswordChangeModal;
